import {
    DevicePhoneBookAssignItem,
    DevicePhoneBookTabProps,
    generateColumns,
    useStyles
} from "./DevicePhoneBookTab.utils";
import React, {useCallback, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import Grid from '@material-ui/core/Grid';
import classNames from "classnames";
import {ReduxState} from "../../store/types";
import {actions} from "../../store";
import {YesNo} from "../../store/types/CallScreening";
import DataGrid from '../../components/DataGrid/DataGrid';
import {usePermissions} from "../../hooks/usePermissions";
import {Permission, PermissionType} from "../../store/types/Permission";
import {DeviceProfileDetailsTabFormType} from "./DeviceProfileDetailsTab.utils";
import {useFormikContext} from "formik";
import DeviceProfileDetailsActions from "./DeviceProfileDetailsActions";
import {Info} from '@material-ui/icons';

const DevicePhoneBookTab: React.FC<DevicePhoneBookTabProps> = (
) => {

    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();
    
    const permission = usePermissions(...Permission.Inventory.DeviceProfiles.value);
    const i_customer: number = useSelector((state: ReduxState) => state.generic?.sessionData?.i_customer ?? 0);
    const permissionToPhoneBooks = usePermissions(...Permission.Inventory.DevicePhoneBooks.value);

    const {
        uaProfileMetaInfo,
        phoneDirectories
    } = useSelector((state: ReduxState) => state.devices);

    const fetchData = () => {
        dispatch(
            actions.getDevicePhoneBookList.request({
                get_total: 1,
                get_total_records: YesNo.Yes,
                i_customer: i_customer,
                limit: 1000,
                offset: 0,
            })
        );
    };

    useEffect(() => {
        if(phoneDirectories === undefined) {
            fetchData();
        }
    }, [phoneDirectories]);

    const { values, setFieldValue } = useFormikContext<DeviceProfileDetailsTabFormType>();

    const totalSupportedPhoneBooks = useMemo(() => {
        return uaProfileMetaInfo?.remote_directories?.total_supported ?? 0;
    }, [uaProfileMetaInfo]);

    const isLimitReached = useMemo(() => {
        const assignedItemsCount = values?.directories?.filter(e => e.enabled)?.length ?? 0;
        return assignedItemsCount >= totalSupportedPhoneBooks;
    }, [values, totalSupportedPhoneBooks]);

    const onSwitch = useCallback((enable: boolean, object: DevicePhoneBookAssignItem) => {
        const arrayIndex = values.directories.findIndex(e => e.i_ua_config_directory === object.i_ua_config_directory);
        setFieldValue('directories[' + arrayIndex + '].enabled', enable);
    }, [values, setFieldValue]);

    const dragEnabled = useMemo(() => {
        return permission === PermissionType.Visible && totalSupportedPhoneBooks > 0;
    }, [permission, totalSupportedPhoneBooks]);

    const columns = useMemo(() => generateColumns(
        isLimitReached,
        t,
        classes,
        permissionToPhoneBooks,
        onSwitch
    ), [isLimitReached, permissionToPhoneBooks, onSwitch, t]);

    return (
        <Grid
            container
            spacing={3}
            className={classNames(
                classes.itemsContainer
            )}
        > 
            <div className={classes.limitContainer}>
                <div className={classes.limitIcon}><Info /></div>
                <div className={classes.limitLabel}>{t('screens:devicePhoneBooks.activePhonebooksLimitText', {
                    value: totalSupportedPhoneBooks
                })}
                </div>
            </div>
            <DataGrid<DevicePhoneBookAssignItem>
                classes={{
                    tableContainer: classNames(classes.rulesTableContainer,
                        dragEnabled ? classes.draggableTableContainer : classes.notDraggableTableContainer)
                }}
                dragColumnStyle={classes.dragColumnStyle}
                dragHeaderStyle={classes.dragColumnStyle}
                columns={columns}
                data={values.directories}
                rowCount={values.directories.length}
                pageSizeOptions={[values.directories.length]}
                initialPageSize={Number.MAX_VALUE}
                centeredRows
                hideFooter
                dragEnabled={dragEnabled}
                dragHeader={t('common:order')}
                getItemId={(r: DevicePhoneBookAssignItem) => r.i_ua_config_directory}
                onChangeItemPosition={(startIndex, endIndex) => {
                    const isAsc = startIndex > endIndex;
                    let i = startIndex + (isAsc ? -1 : +1);
                    const dirList = [...values.directories];
                    while(i != (endIndex + (isAsc ? -1 : +1))) {
                        const newIndex = (dirList[i].orderIndex || 0) + (isAsc ? +1 : -1);
                        dirList[i].orderIndex = newIndex;
                        i = i + (isAsc ? -1 : +1);
                    }
                    dirList[startIndex].orderIndex = endIndex;
                    dirList.sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
                    setFieldValue('directories', dirList);
                }}
            />
            <DeviceProfileDetailsActions />
        </Grid>
    );
}
export default DevicePhoneBookTab;
